import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listPosts, updatePost, deletePost } from "@/lib/blog";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import type { BlogPost } from "@/content/blog/posts";

type Row = BlogPost & { id: string; published: boolean };

export default function AdminPosts() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setPosts((await listPosts()) as Row[]);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePublished = async (p: Row) => {
    try {
      await updatePost(p.id, { published: !p.published });
      setPosts((xs) => xs.map((x) => (x.id === p.id ? { ...x, published: !x.published } : x)));
    } catch (e: any) { toast.error(e.message); }
  };

  const remove = async (p: Row) => {
    if (!confirm(`Delete "${p.title}"?`)) return;
    try {
      await deletePost(p.id);
      setPosts((xs) => xs.filter((x) => x.id !== p.id));
      toast.success("Deleted");
    } catch (e: any) { toast.error(e.message); }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#fbf9f4] text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="font-serif text-xl">BabbleBlog admin</h1>
          <div className="flex gap-2">
            <Link to="/blog" className="text-sm text-slate-600 hover:text-[#de0046] self-center">View blog →</Link>
            <Link to="/admin/stats" className="text-sm text-slate-600 hover:text-[#de0046] self-center">Player stats →</Link>
            <Button onClick={() => navigate("/admin/posts/new")} className="bg-[#de0046] hover:bg-[#b8003a]">New post</Button>
            <Button variant="outline" onClick={signOut}>Sign out</Button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? <p>Loading…</p> : posts.length === 0 ? (
          <p className="text-slate-600">No posts yet. Click "New post" to create one.</p>
        ) : (
          <ul className="divide-y divide-slate-200 bg-white border border-slate-200 rounded-xl">
            {posts.map((p) => (
              <li key={p.id} className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <Link to={`/admin/posts/${p.id}`} className="font-medium hover:text-[#de0046] block truncate">{p.title}</Link>
                  <p className="text-xs text-slate-500 mt-0.5">{p.date} · /{p.slug} · {p.published ? "Published" : "Draft"}</p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500">Published</span>
                  <Switch checked={p.published} onCheckedChange={() => togglePublished(p)} />
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/admin/posts/${p.id}`)}>Edit</Button>
                <Button variant="outline" size="sm" onClick={() => remove(p)} className="text-red-600 hover:text-red-700">Delete</Button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
